/**
 * domain-flytt.ts — innhold for /domener/flytt («Overfør domene»), norsk + engelsk
 *
 * ⚠ PLASSHOLDERE: flytteregler, sjekkliste, tidsforbruk og om registreringen forlenges må
 * bekreftes mot registrar og Norid (for .no) før publisering.
 *
 * SIKKERHET: overføringskoden (EPP/auth-kode) skal ALDRI sendes på e-post. Skjemaet i
 * heroen ber bare om domenenavnet; siden sier at vi sender en sikker lenke for koden.
 */

import type { Locale } from '@/types/landing';
import type { DomainTransferPageContent } from '@/types/domain';
import { DOMAIN_CONTACT } from './domain-shared';

const no: DomainTransferPageContent = {
  seoTitle: 'Overfør domene — inDomain',
  breadcrumb: {
    label: 'Brødsmulesti',
    links: [{ label: 'Hjem', href: '/' }, { label: 'Domener', href: '/domener/sok' }],
    current: 'Overfør domene',
  },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'steg', label: 'Slik flytter du' },
      { id: 'sjekkliste', label: 'Sjekkliste' },
      { id: 'priser', label: 'Priser' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se stegene', href: '#steg' },
  },
  hero: {
    heading: 'Overfør domene',
    subheading: 'Flytt ditt eksisterende domene til inSuite.',
    primaryCta: { label: 'Se stegene', href: '#steg' },
    secondaryCta: { label: 'Se flytteprisene', href: '#priser' },
    illustrationLabel: 'Illustrasjon av et adressefelt, en klode og en prislapp',
  },
  request: {
    label: 'Domenet du vil flytte',
    placeholder: 'dittnavn.no',
    button: 'Be om flytting',
    warning: 'Send aldri overføringskoden på e-post. Vi sender deg en sikker lenke der du kan oppgi den.',
    errors: {
      empty: 'Skriv inn domenet du vil flytte.',
      invalid: 'Skriv inn et gyldig domenenavn med endelse, for eksempel dittnavn.no.',
    },
    contactHref: 'mailto:admin@ntune.no?subject=Flytte%20domene%20{domain}',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'Samle domenene dine.',
    body: [
      'Når domenet ligger hos oss, styrer du det sammen med hostingen din, med én innlogging og én faktura.',
      'Flyttingen skjer uten at nettstedet eller e-posten må stå stille, så lenge DNS-oppføringene beholdes.',
    ],
  },
  steps: {
    eyebrow: 'Slik flytter du',
    heading: 'Fire steg til flyttet domene',
    intro: 'Du trenger overføringskoden fra din nåværende leverandør.',
    panelLabel: 'Illustrasjon av et adressefelt og en klode',
    items: [
      { title: 'Hent overføringskoden', body: 'Lås opp domenet og hent overføringskoden hos din nåværende leverandør.' },
      { title: 'Be om flytting hos oss', body: 'Oppgi domenenavnet, så sender vi deg en sikker lenke der du legger inn koden.' },
      { title: 'Godkjenn flyttingen', body: 'Du godkjenner flyttingen når du får forespørsel på e-post.' },
      { title: 'Domenet er hos oss', body: 'Når flyttingen er ferdig, administrerer du domenet hos oss.' },
    ],
  },
  checklist: {
    eyebrow: 'Sjekkliste',
    heading: 'Før du flytter',
    intro: 'Gjør dette først, så går flyttingen smertefritt.',
    panelLabel: 'Illustrasjon av et adressefelt og en klode',
    items: [
      { title: 'Lås opp domenet', body: 'Domenet er vanligvis låst mot flytting. Lås det opp hos din nåværende leverandør.' },
      { title: 'Hent overføringskoden', body: 'Koden bekrefter at du eier domenet. Behandle den som et passord.' },
      { title: 'Sjekk e-postadressen i WHOIS', body: 'Godkjenningen sendes til e-postadressen som er registrert på domenet. Den må være riktig og fungere.' },
      { title: 'Behold DNS-oppføringene', body: 'Ikke bytt navneservere før flyttingen er ferdig, så nettsted og e-post fortsetter å virke.' },
      { title: 'Sjekk at domenet kan flyttes', body: 'Nylig registrerte eller nylig flyttede domener kan ofte ikke flyttes med en gang.' },
    ],
  },
  prices: {
    eyebrow: 'Priser',
    heading: 'Hva koster flytting?',
    intro: 'Flyttepris per domeneendelse. Se alle endelser i den fullstendige listen.',
    columns: { tld: 'Domeneendelse', register: 'Registrering', renew: 'Fornyelse', transfer: 'Flytting' },
    footnote: '*Prisene er veiledende, per år og eksklusiv mva.',
    popular: 'Mest populære',
  },
  promo: { title: 'Trenger du et nytt domene i stedet?', body: 'Søk etter navnet, så finner vi riktig endelse.', cta: { label: 'Søk etter domener', href: '/domener/sok' } },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      { question: 'Blir nettstedet mitt borte under flyttingen?', answer: 'Nei, så lenge DNS-oppføringene beholdes. Vi hjelper deg med å flytte dem, så nettsted og e-post fortsetter å virke.' },
      { question: 'Hvor lang tid tar det?', answer: 'Det varierer med endelse og leverandør, fra noen timer til noen dager. Ta kontakt for et estimat.' },
      { question: 'Hva koster flytting?', answer: 'Se tabellen over. Prisen avhenger av endelsen.' },
      { question: 'Hvordan får jeg overføringskoden?', answer: 'Du henter den hos din nåværende leverandør, vanligvis i domeneinnstillingene. Den skal ikke sendes på e-post.' },
      { question: 'Forlenges registreringen når jeg flytter?', answer: 'For mange endelser forlenges registreringen med ett år. Reglene varierer, så ta kontakt for din endelse.' },
      { question: 'Kan jeg flytte et .no-domene?', answer: 'Ja, men .no har egne regler for flytting og krav til tilknytning til Norge. Ta kontakt for detaljer.' },
    ],
  },
  cta: { heading: 'Klar til å flytte?', body: 'Be om flytting, så hjelper vi deg gjennom hele prosessen.', primary: { label: 'Kontakt oss', href: DOMAIN_CONTACT } },
};

const en: DomainTransferPageContent = {
  seoTitle: 'Transfer a domain — inDomain',
  breadcrumb: {
    label: 'Breadcrumb',
    links: [{ label: 'Home', href: '/' }, { label: 'Domains', href: '/domener/sok' }],
    current: 'Transfer a domain',
  },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'steg', label: 'How to transfer' },
      { id: 'sjekkliste', label: 'Checklist' },
      { id: 'priser', label: 'Pricing' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See the steps', href: '#steg' },
  },
  hero: {
    heading: 'Transfer a domain',
    subheading: 'Move your existing domain to inSuite.',
    primaryCta: { label: 'See the steps', href: '#steg' },
    secondaryCta: { label: 'See transfer pricing', href: '#priser' },
    illustrationLabel: 'Illustration of an address bar, a globe and a price tag',
  },
  request: {
    label: 'The domain you want to transfer',
    placeholder: 'yourname.no',
    button: 'Request transfer',
    warning: 'Never send the transfer code by email. We will send you a secure link where you can enter it.',
    errors: {
      empty: 'Enter the domain you want to transfer.',
      invalid: 'Enter a valid domain name with an extension, for example yourname.no.',
    },
    contactHref: 'mailto:admin@ntune.no?subject=Transfer%20domain%20{domain}',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'Bring your domains together.',
    body: [
      'When the domain is with us, you manage it together with your hosting, with one login and one invoice.',
      'The transfer happens without your website or email having to stop, as long as the DNS records are kept.',
    ],
  },
  steps: {
    eyebrow: 'How to transfer',
    heading: 'Four steps to a transferred domain',
    intro: 'You need the transfer code from your current provider.',
    panelLabel: 'Illustration of an address bar and a globe',
    items: [
      { title: 'Get the transfer code', body: 'Unlock the domain and get the transfer code from your current provider.' },
      { title: 'Request the transfer with us', body: 'Give us the domain name, and we send you a secure link where you enter the code.' },
      { title: 'Approve the transfer', body: 'You approve the transfer when you get a request by email.' },
      { title: 'The domain is with us', body: 'When the transfer is done, you manage the domain with us.' },
    ],
  },
  checklist: {
    eyebrow: 'Checklist',
    heading: 'Before you transfer',
    intro: 'Do this first, and the transfer goes smoothly.',
    panelLabel: 'Illustration of an address bar and a globe',
    items: [
      { title: 'Unlock the domain', body: 'The domain is usually locked against transfers. Unlock it with your current provider.' },
      { title: 'Get the transfer code', body: 'The code confirms that you own the domain. Treat it like a password.' },
      { title: 'Check the email in WHOIS', body: 'The approval is sent to the email address registered on the domain. It must be correct and working.' },
      { title: 'Keep your DNS records', body: 'Do not change nameservers until the transfer is done, so your website and email keep working.' },
      { title: 'Check that the domain can be transferred', body: 'Recently registered or recently transferred domains often cannot be transferred straight away.' },
    ],
  },
  prices: {
    eyebrow: 'Pricing',
    heading: 'What does a transfer cost?',
    intro: 'Transfer price per domain extension. See all extensions in the full list.',
    columns: { tld: 'Extension', register: 'Registration', renew: 'Renewal', transfer: 'Transfer' },
    footnote: '*Prices are indicative, per year and exclude VAT.',
    popular: 'Most popular',
  },
  promo: { title: 'Need a new domain instead?', body: 'Search for the name, and we find the right extension.', cta: { label: 'Search for domains', href: '/domener/sok' } },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      { question: 'Will my website go down during the transfer?', answer: 'No, as long as the DNS records are kept. We help you move them, so your website and email keep working.' },
      { question: 'How long does it take?', answer: 'It varies with the extension and provider, from a few hours to a few days. Get in touch for an estimate.' },
      { question: 'What does a transfer cost?', answer: 'See the table above. The price depends on the extension.' },
      { question: 'How do I get the transfer code?', answer: 'You get it from your current provider, usually in the domain settings. It must not be sent by email.' },
      { question: 'Is the registration extended when I transfer?', answer: 'For many extensions the registration is extended by one year. The rules vary, so get in touch for your extension.' },
      { question: 'Can I transfer a .no domain?', answer: 'Yes, but .no has its own transfer rules and requires a connection to Norway. Get in touch for details.' },
    ],
  },
  cta: { heading: 'Ready to transfer?', body: 'Request a transfer, and we will guide you through the whole process.', primary: { label: 'Contact us', href: DOMAIN_CONTACT } },
};

export const DOMAIN_FLYTT: Record<Locale, DomainTransferPageContent> = { no, en };
